import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Button, Stack, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, MenuItem, Chip, Alert
} from '@mui/material';
import { Plus, SquarePen, Trash2, Layers, X } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
    getCancellationPolicyTypes,
    createCancellationPolicyType,
    updateCancellationPolicyType,
    deleteCancellationPolicyType,
    getRefundPolicyTiersByPolicyId,
    saveRefundPolicyTiers,
    deleteRefundPolicyTier
} from '../../Services/ContentHub/CancellationPolicyTypesApi';
import { stripHtml } from "string-strip-html";
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
import DeleteModal from '../../Components/Modals/DeleteModal';

const quillModules = {
    toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['link'],
        ['clean']
    ]
};

const policyTypeOptions = [
    { value: 'Free', label: 'Free' },
    { value: 'Flexible', label: 'Flexible' },
    { value: 'Moderate', label: 'Moderate' }
];

const getPolicyChipColor = (type) => {
    switch (type) {
        case 'Free':
            return { bg: '#E8F5E9', color: '#2E7D32', border: '#C8E6C9' };
        case 'Flexible':
            return { bg: '#E3F2FD', color: '#1565C0', border: '#BBDEFB' };
        case 'Moderate':
            return { bg: '#FFF3E0', color: '#E65100', border: '#FFE0B2' };
        default:
            return { bg: '#F5F5F5', color: '#616161', border: '#E0E0E0' };
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const CancellationPolicyTypes = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [policyTypes, setPolicyTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentPolicy, setCurrentPolicy] = useState(null);
    const [formData, setFormData] = useState({
        policyType: '',
        description: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    // Delete modal states
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Refund Policy Tiers states
    const [tierModalOpen, setTierModalOpen] = useState(false);
    const [selectedPolicyId, setSelectedPolicyId] = useState('');
    const [existingTiers, setExistingTiers] = useState([]);
    const [loadingExistingTiers, setLoadingExistingTiers] = useState(false);
    const [tierRows, setTierRows] = useState([
        { formDays: '', toDays: '', refundPercentage: '', refundDescription: '' }
    ]);
    const [savingTiers, setSavingTiers] = useState(false);
    const [deletingTierId, setDeletingTierId] = useState(null);

    const loadTiersForPolicy = async (policyId) => {
        if (!policyId) {
            setExistingTiers([]);
            setTierRows([{ id: null, formDays: '', toDays: '', refundPercentage: '', refundDescription: '' }]);
            return;
        }
        try {
            setLoadingExistingTiers(true);
            const res = await getRefundPolicyTiersByPolicyId(policyId);
            let list = [];
            if (res && res.success && Array.isArray(res.data)) {
                list = res.data;
            } else if (Array.isArray(res)) {
                list = res;
            } else if (res && Array.isArray(res.data)) {
                list = res.data;
            }
            setExistingTiers(list);
            if (list.length > 0) {
                setTierRows(list.map(t => ({
                    id: t.id,
                    formDays: String(t.formDays ?? ''),
                    toDays: String(t.toDays ?? ''),
                    refundPercentage: String(t.refundPercentage ?? ''),
                    refundDescription: t.refundDescription || ''
                })));
            } else {
                setTierRows([{ id: null, formDays: '', toDays: '', refundPercentage: '', refundDescription: '' }]);
            }
        } catch (err) {
            console.error("Error loading policy tiers:", err);
            setExistingTiers([]);
            setTierRows([{ id: null, formDays: '', toDays: '', refundPercentage: '', refundDescription: '' }]);
        } finally {
            setLoadingExistingTiers(false);
        }
    };

    const handleOpenTierModal = (policy = null) => {
        const nonFreePolicies = policyTypes.filter(p => p.policyType?.toLowerCase() !== 'free');
        const targetPolicyId = policy ? policy.id : (nonFreePolicies.length > 0 ? nonFreePolicies[0].id : '');
        setSelectedPolicyId(targetPolicyId);
        setTierRows([
            { id: null, formDays: '', toDays: '', refundPercentage: '', refundDescription: '' }
        ]);
        setTierModalOpen(true);
        if (targetPolicyId) {
            loadTiersForPolicy(targetPolicyId);
        }
    };

    const handleCloseTierModal = () => {
        setTierModalOpen(false);
        setSelectedPolicyId('');
        setExistingTiers([]);
        setTierRows([
            { id: null, formDays: '', toDays: '', refundPercentage: '', refundDescription: '' }
        ]);
    };

    const handlePolicySelectChange = (e) => {
        const pId = e.target.value;
        setSelectedPolicyId(pId);
        loadTiersForPolicy(pId);
    };

    const handleAddTierRow = () => {
        setTierRows(prev => [
            ...prev,
            { id: null, formDays: '', toDays: '', refundPercentage: '', refundDescription: '' }
        ]);
    };

    const handleEditTableTier = (tier) => {
        const existsIndex = tierRows.findIndex(r => r.id === tier.id);
        if (existsIndex === -1) {
            setTierRows(prev => [
                ...prev,
                {
                    id: tier.id,
                    formDays: String(tier.formDays ?? ''),
                    toDays: String(tier.toDays ?? ''),
                    refundPercentage: String(tier.refundPercentage ?? ''),
                    refundDescription: tier.refundDescription || ''
                }
            ]);
            showSnackbar("Tier loaded into edit form below", "info");
        } else {
            showSnackbar("Tier is already loaded in the edit section below", "info");
        }
    };

    const handleRemoveTierRow = (index) => {
        const row = tierRows[index];
        if (row.id) {
            handleDeleteExistingTier(row.id);
        } else {
            setTierRows(prev => prev.filter((_, idx) => idx !== index));
        }
    };

    const handleTierRowChange = (index, field, value) => {
        setTierRows(prev => {
            const updated = [...prev];
            const currentRow = { ...updated[index], [field]: value };

            if (field === 'formDays' || field === 'toDays' || field === 'refundPercentage') {
                const formD = field === 'formDays' ? value : currentRow.formDays;
                const toD = field === 'toDays' ? value : currentRow.toDays;
                const pct = field === 'refundPercentage' ? value : currentRow.refundPercentage;

                if (pct && (formD || toD)) {
                    if (formD && toD) {
                        currentRow.refundDescription = `${pct}% refund between ${formD} and ${toD} days before check-in`;
                    } else if (formD) {
                        currentRow.refundDescription = `${pct}% refund up to ${formD} days before check-in`;
                    }
                }
            }

            updated[index] = currentRow;
            return updated;
        });
    };

    const handleSaveTiers = async () => {
        if (!selectedPolicyId) {
            showSnackbar("Please select a cancellation policy type", "error");
            return;
        }

        if (tierRows.length === 0) {
            showSnackbar("Please add at least one tier row", "error");
            return;
        }

        for (let i = 0; i < tierRows.length; i++) {
            const r = tierRows[i];
            if (r.formDays === '' || r.toDays === '' || r.refundPercentage === '' || !r.refundDescription.trim()) {
                showSnackbar(`Please fill in all fields for Tier #${i + 1}`, "error");
                return;
            }
        }

        const payload = tierRows.map(r => {
            const item = {
                refundPolicyId: Number(selectedPolicyId),
                refundDescription: r.refundDescription.trim(),
                formDays: Number(r.formDays),
                toDays: Number(r.toDays),
                refundPercentage: Number(r.refundPercentage)
            };
            if (r.id) {
                item.id = r.id;
            }
            return item;
        });

        try {
            setSavingTiers(true);
            const res = await saveRefundPolicyTiers(payload);
            if (res && (res.success || res.data || Array.isArray(res))) {
                showSnackbar("Refund policy tiers saved successfully!", "success");
                loadTiersForPolicy(selectedPolicyId);
            } else {
                showSnackbar(res?.message || "Failed to save refund policy tiers", "error");
            }
        } catch (err) {
            console.error("Error saving refund policy tiers:", err);
            const errMsg = err.response?.data?.message || err.message || "Failed to save refund policy tiers";
            showSnackbar(errMsg, "error");
        } finally {
            setSavingTiers(false);
        }
    };

    const handleDeleteExistingTier = async (tierId) => {
        try {
            setDeletingTierId(tierId);
            const res = await deleteRefundPolicyTier(tierId);
            if (res && (res.success || !res.error)) {
                showSnackbar("Refund policy tier deleted successfully!", "success");
                loadTiersForPolicy(selectedPolicyId);
            } else {
                showSnackbar(res?.message || "Failed to delete refund policy tier", "error");
            }
        } catch (err) {
            console.error("Error deleting refund policy tier:", err);
            const errMsg = err.response?.data?.message || err.message || "Failed to delete refund policy tier";
            showSnackbar(errMsg, "error");
        } finally {
            setDeletingTierId(null);
        }
    };

    // Fetch policy types
    const fetchPolicyTypes = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getCancellationPolicyTypes();
            
            let list = [];
            if (response && response.success && Array.isArray(response.data)) {
                list = response.data;
            } else if (Array.isArray(response)) {
                list = response;
            } else if (response && Array.isArray(response.data)) {
                list = response.data;
            }

            setPolicyTypes(list);
        } catch (err) {
            console.error("Error fetching cancellation policy types:", err);
            setError("Failed to fetch cancellation policy types. Please try again later.");
            setPolicyTypes([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPolicyTypes();
    }, [fetchPolicyTypes]);

    const handleOpenModal = (policy = null) => {
        setFieldErrors({});
        if (policy) {
            setIsEdit(true);
            setCurrentPolicy(policy);
            setFormData({
                policyType: policy.policyType || '',
                description: policy.description || ''
            });
        } else {
            setIsEdit(false);
            setCurrentPolicy(null);
            setFormData({
                policyType: '',
                description: ''
            });
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setFieldErrors({});
        setOpenModal(false);
        setIsEdit(false);
        setCurrentPolicy(null);
        setFormData({
            policyType: '',
            description: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleQuillChange = (value) => {
        setFormData(prev => ({
            ...prev,
            description: value
        }));
        if (fieldErrors.description) {
            setFieldErrors(prev => ({
                ...prev,
                description: null
            }));
        }
    };

    const handleSubmit = async () => {
        const errs = {};
        if (!formData.policyType) errs.policyType = "Policy Type is required";
        
        // Strip tags for validation to check if description has real text
        const plainDesc = formData.description ? stripHtml(formData.description).result.trim() : '';
        if (!formData.description || !plainDesc) errs.description = "Description is required";

        if (Object.keys(errs).length > 0) {
            setFieldErrors(errs);
            showSnackbar("Please fill in all required fields", "error");
            return;
        }

        setSubmitting(true);
        setFieldErrors({});

        try {
            let response;
            if (isEdit) {
                response = await updateCancellationPolicyType(currentPolicy.id, formData);
            } else {
                response = await createCancellationPolicyType(formData);
            }

            if (response && (response.success || response.id || response.data)) {
                showSnackbar(
                    isEdit
                        ? "Cancellation policy type updated successfully!"
                        : "Cancellation policy type created successfully!",
                    "success"
                );
                handleCloseModal();
                fetchPolicyTypes();
            } else {
                showSnackbar(response?.message || `Failed to ${isEdit ? 'update' : 'create'} policy type.`, "error");
            }
        } catch (err) {
            console.error("Error saving cancellation policy type:", err);
            const serverError = err.response?.data;
            const errMsg = serverError?.errors?.[0] ||
                serverError?.details?.[0]?.message ||
                serverError?.message ||
                err.message ||
                `Failed to ${isEdit ? 'update' : 'create'} cancellation policy type`;

            showSnackbar(errMsg, "error");

            if (serverError && serverError.details && Array.isArray(serverError.details)) {
                const errors = {};
                serverError.details.forEach(detail => {
                    errors[detail.field] = detail.message;
                });
                setFieldErrors(errors);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenDelete = (id) => {
        setIdToDelete(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!idToDelete) return;

        try {
            setIsDeleting(true);
            setDeleteError(null);
            const response = await deleteCancellationPolicyType(idToDelete);
            if (response && (response.success || response.status === 200 || !response.error)) {
                showSnackbar("Cancellation policy type deleted successfully!", "success");
                setDeleteModalOpen(false);
                setIdToDelete(null);
                fetchPolicyTypes();
            } else {
                const errMsg = response?.message || "Failed to delete cancellation policy type";
                setDeleteError(errMsg);
                showSnackbar(errMsg, "error");
            }
        } catch (err) {
            console.error("Delete Error:", err);
            const errorMessage = err.response?.data?.message || err.message || "An error occurred while deleting policy type";
            setDeleteError(errorMessage);
            showSnackbar(errorMessage, "error");
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Cancellation Policy Types
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <PermissionWrapper permission="create" mode="disable">
                            <Button
                                disableElevation
                                disableRipple
                                disableFocusRipple
                                disableTouchRipple
                                variant="contained"
                                startIcon={<Plus size={18} />}
                                onClick={() => handleOpenModal()}
                                sx={{
                                    bgcolor: '#FFBD65',
                                    px: 2.5,
                                    color: '#fff',
                                    borderRadius: '6px',
                                    height: '40px',
                                    textTransform: 'none',
                                    fontSize: '0.85rem'
                                }}
                            >
                                Add Cancellation Policy Type
                            </Button>
                        </PermissionWrapper>
                    </Stack>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                {/* Table Section */}
                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1000 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>
                                        S.No
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '150px' }}>
                                        Policy Type
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>
                                        Description
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>
                                        Created By
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>
                                        Updated By
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>
                                        Created At
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>
                                        Updated At
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }}>
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : policyTypes.length > 0 ? (
                                    policyTypes.map((item, idx) => {
                                        const chipStyle = getPolicyChipColor(item.policyType);
                                        const descText = item.description ? stripHtml(item.description).result : '';
                                        return (
                                            <TableRow key={item.id} sx={{
                                                transition: 'background-color 0.2s',
                                                '& td': { borderBottom: '1px solid #F0F0F0' },
                                                '&:last-child td': { borderBottom: 0 }
                                            }}>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>
                                                    {idx + 1}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem' }}>
                                                    <Chip
                                                        label={item.policyType}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: chipStyle.bg,
                                                            color: chipStyle.color,
                                                            border: `1px solid ${chipStyle.border}`,
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            borderRadius: '4px'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>
                                                    {descText.length > 120 ? `${descText.substring(0, 120)}...` : descText}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                    {item.cuName || '-'}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                    {item.uuName || '-'}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                    {formatDate(item.createdAt)}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                    {formatDate(item.updatedAt)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                        {item.policyType?.toLowerCase() !== 'free' && (
                                                            <PermissionWrapper permission="edit" mode="disable">
                                                                <Tooltip title="Manage Refund Tiers">
                                                                    <IconButton
                                                                        disableFocusRipple
                                                                        disableRipple
                                                                        disableTouchRipple
                                                                        size="small"
                                                                        sx={{ color: '#3B82F6' }}
                                                                        onClick={() => handleOpenTierModal(item)}
                                                                    >
                                                                        <Layers size={16} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </PermissionWrapper>
                                                        )}
                                                        <PermissionWrapper permission="edit" mode="disable">
                                                            <Tooltip title="Edit">
                                                                <IconButton
                                                                    disableFocusRipple
                                                                    disableRipple
                                                                    disableTouchRipple
                                                                    size="small"
                                                                    sx={{ color: '#FFBD65' }}
                                                                    onClick={() => handleOpenModal(item)}
                                                                >
                                                                    <SquarePen size={16} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </PermissionWrapper>
                                                        <PermissionWrapper permission="delete" mode="disable">
                                                            <Tooltip title="Delete">
                                                                <IconButton
                                                                    disableFocusRipple
                                                                    disableRipple
                                                                    disableTouchRipple
                                                                    size="small"
                                                                    sx={{ color: '#FF4D49' }}
                                                                    onClick={() => handleOpenDelete(item.id)}
                                                                >
                                                                    <Trash2 size={16} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </PermissionWrapper>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No cancellation policy types found. Click "Add Cancellation Policy Type" to create one.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Add/Edit Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Cancellation Policy Type' : 'Add Cancellation Policy Type'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>
                                    Policy Type *
                                </Typography>
                                <TextField
                                    select
                                    fullWidth
                                    name="policyType"
                                    value={formData.policyType}
                                    onChange={handleInputChange}
                                    size="small"
                                    error={Boolean(fieldErrors.policyType)}
                                    helperText={fieldErrors.policyType}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                >
                                    {policyTypeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.85rem' }}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>
                                    Description *
                                </Typography>
                                <Box sx={{
                                    border: fieldErrors.description ? '1px solid #FF4D49' : 'none',
                                    borderRadius: '6px',
                                    '& .ql-toolbar': { borderTopLeftRadius: '6px', borderTopRightRadius: '6px', borderColor: '#E0E0E0' },
                                    '& .ql-container': { borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', borderColor: '#E0E0E0', fontSize: '0.85rem', minHeight: '180px' }
                                }}>
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.description}
                                        onChange={handleQuillChange}
                                        placeholder="Enter policy description here..."
                                        style={{
                                            height: '220px',
                                            marginBottom: '45px'
                                        }}
                                        modules={quillModules}
                                    />
                                </Box>
                                {fieldErrors.description && (
                                    <Typography variant="caption" sx={{ color: '#FF4D49', mt: 0.5, display: 'block', fontSize: '0.75rem' }}>
                                        {fieldErrors.description}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button
                            disableElevation
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            onClick={handleCloseModal}
                            sx={{
                                borderRadius: '6px',
                                textTransform: 'none',
                                color: '#222',
                                px: 2.5,
                                fontSize: '0.85rem'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disableElevation
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                color: '#fff',
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Refund Policy Tiers Modal */}
                <Dialog open={tierModalOpen} onClose={handleCloseTierModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '8px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Layers size={20} color="#FFBD65" />
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                Manage Refund Policy Tiers
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={handleCloseTierModal}>
                            <X size={18} />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers sx={{ p: 3 }}>
                        <Stack spacing={3}>
                            {/* Policy Selection */}
                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 600, color: '#333', fontSize: '0.85rem' }}>
                                    Select Cancellation Policy *
                                </Typography>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    value={selectedPolicyId}
                                    onChange={handlePolicySelectChange}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                >
                                    {policyTypes.filter(p => p.policyType?.toLowerCase() !== 'free').map((policy) => (
                                        <MenuItem key={policy.id} value={policy.id} sx={{ fontSize: '0.85rem' }}>
                                            {policy.policyType}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>

                            {/* Existing Tiers Section */}
                            {selectedPolicyId && (
                                <Box>
                                    <Typography sx={{ mb: 1.5, fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>
                                        Existing Policy Tiers
                                    </Typography>
                                    {loadingExistingTiers ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </Box>
                                    ) : existingTiers.length > 0 ? (
                                        <Paper variant="outlined" sx={{ borderRadius: '6px', overflow: 'hidden' }}>
                                            <Table size="small">
                                                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569' }}>From Days</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569' }}>To Days</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569' }}>Refund %</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569' }}>Description</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569' }}>Action</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {existingTiers.map((tier) => (
                                                        <TableRow key={tier.id}>
                                                            <TableCell sx={{ fontSize: '0.8rem' }}>{tier.formDays}</TableCell>
                                                            <TableCell sx={{ fontSize: '0.8rem' }}>{tier.toDays}</TableCell>
                                                            <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#2E7D32' }}>{tier.refundPercentage}%</TableCell>
                                                            <TableCell sx={{ fontSize: '0.8rem' }}>{tier.refundDescription}</TableCell>
                                                            <TableCell align="right">
                                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{ color: '#FFBD65' }}
                                                                        onClick={() => handleEditTableTier(tier)}
                                                                        title="Edit Tier"
                                                                    >
                                                                        <SquarePen size={15} />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{ color: '#FF4D49' }}
                                                                        onClick={() => handleDeleteExistingTier(tier.id)}
                                                                        disabled={deletingTierId === tier.id}
                                                                        title="Delete Tier"
                                                                    >
                                                                        {deletingTierId === tier.id ? <CircularProgress size={14} color="inherit" /> : <Trash2 size={15} />}
                                                                    </IconButton>
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Paper>
                                    ) : (
                                        <Typography sx={{ fontSize: '0.8rem', color: '#888', fontStyle: 'italic' }}>
                                            No tiers added yet for this policy. Add tiers below.
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {/* Add / Edit Tiers Section */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>
                                        Add / Edit Refund Tiers (Multiple at once)
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<Plus size={16} />}
                                        onClick={handleAddTierRow}
                                        sx={{
                                            textTransform: 'none',
                                            color: '#FFBD65',
                                            fontWeight: 600,
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        Add Row
                                    </Button>
                                </Box>

                                <Stack spacing={2}>
                                    {tierRows.map((row, index) => (
                                        <Paper
                                            key={index}
                                            variant="outlined"
                                            sx={{ p: 2, borderRadius: '6px', bgcolor: '#FAFBFD', position: 'relative' }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Chip
                                                    label={row.id ? `Tier #${index + 1} (Editing)` : `Tier #${index + 1} (New)`}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        height: 20,
                                                        bgcolor: row.id ? '#FFF3E0' : '#EEF2F6',
                                                        color: row.id ? '#E65100' : '#475569',
                                                        fontWeight: 600
                                                    }}
                                                />
                                                {tierRows.length > 1 && (
                                                    <IconButton size="small" onClick={() => handleRemoveTierRow(index)} sx={{ color: '#FF4D49' }}>
                                                        <Trash2 size={16} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
                                                <TextField
                                                    label="From Days (formDays)"
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    value={row.formDays}
                                                    onChange={(e) => handleTierRowChange(index, 'formDays', e.target.value)}
                                                    placeholder="e.g. 30"
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '0.85rem' } }}
                                                />
                                                <TextField
                                                    label="To Days (toDays)"
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    value={row.toDays}
                                                    onChange={(e) => handleTierRowChange(index, 'toDays', e.target.value)}
                                                    placeholder="e.g. 60"
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '0.85rem' } }}
                                                />
                                                <TextField
                                                    label="Refund % (refundPercentage)"
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    value={row.refundPercentage}
                                                    onChange={(e) => handleTierRowChange(index, 'refundPercentage', e.target.value)}
                                                    placeholder="e.g. 100"
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '0.85rem' } }}
                                                />
                                            </Stack>
                                            <TextField
                                                label="Refund Description (refundDescription)"
                                                size="small"
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={row.refundDescription}
                                                onChange={(e) => handleTierRowChange(index, 'refundDescription', e.target.value)}
                                                placeholder="e.g. 100% refund up to 30 days before check-in"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '0.85rem' } }}
                                            />
                                        </Paper>
                                    ))}
                                </Stack>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5 }}>
                        <Button onClick={handleCloseTierModal} sx={{ textTransform: 'none', color: '#666', borderRadius: '6px' }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveTiers}
                            disabled={savingTiers}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                color: '#fff',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3
                            }}
                        >
                            {savingTiers ? <CircularProgress size={20} color="inherit" /> : 'Save Tiers'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Modal */}
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Cancellation Policy Type"
                    description="Are you sure you want to delete this cancellation policy type? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default CancellationPolicyTypes;
